import { Button, Card, Drawer, Flex, Form, Input, Switch, Typography } from 'antd'
import React, { ReactNode, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LINK, useCreateCampaignStepsContext } from '../campaign-create-steps-context'
import { Title } from '../title'
import { Paragraph } from '../paragraph'
import { FormItem } from '../../../../components/form-item'
import { StoreInput } from '../../../input/store-input'
import { FacebookAccountSelect } from '../../../../facebook/components/facebook-account-select'
import { LeadgenFormSelect } from '../../../../facebook/components/leadgen-form-select'
import { useAuth } from '../../../../auth'
import { ConnectionCard } from '../../../../facebook/components/connection-card'
import clsx from 'clsx'
import { useCheckFacebookLeadSetup } from '../../../../facebook/hooks'
import { SetupCheckResults } from '../../../../facebook/components/setup-check-results'
import { SetupCheckResult } from '../../../../facebook/setup-check'
import { BrandingEditor } from '../../../../public-form/components/branding-editor'
import { configuration, Configuration, defaultConfiguration, withFormLocale } from '../../../../public-form/public-form'
import { DEFAULT_CAMPAIGN_LOCALE } from '../../../campaign-locale'

export function LinkStep() {
    const { t } = useTranslation()
    const { currentStep } = useCreateCampaignStepsContext()

    return (
        <div className={clsx({ hidden: currentStep !== LINK })}>
            <Title>{t('Candidate sources')}</Title>
            <Paragraph>
                {t('Choose how candidates enter this campaign. You can use both sources or set them up later.')}
            </Paragraph>
            <Flex
                vertical
                gap="middle"
                className="mb-6"
            >
                <PublicFormSourceCard />
                <FacebookSourceCard />
            </Flex>
            <LinkStepActions />
        </div>
    )
}

type SourceCardProps = {
    title: string
    description: string
    toggle: ReactNode
    children?: ReactNode
}

function SourceCard({ title, description, toggle, children }: SourceCardProps) {
    return (
        <Card size="small">
            <Flex
                justify="space-between"
                align="center"
            >
                <Typography.Text strong>{title}</Typography.Text>
                {toggle}
            </Flex>
            <Typography.Paragraph
                type="secondary"
                className="mt-3 mb-0"
            >
                {description}
            </Typography.Paragraph>
            {children}
        </Card>
    )
}

function PublicFormSourceCard() {
    const { t } = useTranslation()
    const { user } = useAuth()
    const { formProps } = useCreateCampaignStepsContext()

    const publicEnabled = Form.useWatch('publicFormEnabled', formProps.form)

    const handleToggle = (enabled: boolean) => {
        if (!enabled || formProps.form.getFieldValue('publicForm')) {
            return
        }

        formProps.form.setFieldValue(
            'publicForm',
            withFormLocale(
                defaultConfiguration(formProps.form.getFieldValue('title'), user?.activeCompany?.name ?? ''),
                formProps.form.getFieldValue('locale') ?? DEFAULT_CAMPAIGN_LOCALE
            )
        )
    }

    return (
        <SourceCard
            title={t('Public form')}
            description={t('Let candidates apply from a shared link or QR code and continue in WhatsApp.')}
            toggle={
                <Form.Item
                    name="publicFormEnabled"
                    valuePropName="checked"
                    className="mb-0"
                >
                    <Switch
                        aria-label={t('Public form')}
                        onChange={handleToggle}
                    />
                </Form.Item>
            }
        >
            {publicEnabled && (
                <>
                    <Typography.Text type="secondary">
                        {t(
                            'Your form is created as a draft. Review, publish, and share it after creating the campaign.'
                        )}
                    </Typography.Text>
                    <Form.Item
                        name="publicForm"
                        className="mb-0"
                        rules={[
                            {
                                validator: (_, value) =>
                                    configuration.safeParse(value).success
                                        ? Promise.resolve()
                                        : Promise.reject(
                                              new Error(t('Complete the brand name, heading, and button label.'))
                                          ),
                            },
                        ]}
                    >
                        <CreationBranding />
                    </Form.Item>
                </>
            )}
        </SourceCard>
    )
}

function FacebookSourceCard() {
    const { t } = useTranslation()
    const { user } = useAuth()
    const { formProps } = useCreateCampaignStepsContext()

    const [facebookEnabled, setFacebookEnabled] = useState(false)

    const handleToggle = (enabled: boolean) => {
        setFacebookEnabled(enabled)

        if (!enabled) {
            formProps.form.setFieldValue('accountId', null)
            formProps.form.setFieldValue('formId', null)
        }
    }

    return (
        <SourceCard
            title={t('Facebook lead form')}
            description={t('Receive candidates from your Facebook lead ads.')}
            toggle={
                <Switch
                    aria-label={t('Facebook lead form')}
                    checked={facebookEnabled}
                    onChange={handleToggle}
                />
            }
        >
            {facebookEnabled && (user?.activeCompany?.facebookConnected ? <LinkFacebook /> : <FacebookNotConnected />)}
        </SourceCard>
    )
}

function LinkStepActions() {
    const { t } = useTranslation()
    const { formProps, unlock, isPending, previousStep } = useCreateCampaignStepsContext()

    const publicEnabled = Form.useWatch('publicFormEnabled', formProps.form)

    return (
        <Flex
            gap="middle"
            justify="space-between"
        >
            <Button
                size="large"
                onClick={previousStep}
            >
                {t('Back')}
            </Button>
            <Button
                loading={isPending}
                size="large"
                type="primary"
                onClick={unlock}
                htmlType="submit"
            >
                {t(publicEnabled ? 'Create campaign and review form' : 'Create campaign')}
            </Button>
        </Flex>
    )
}

export function FacebookNotConnected() {
    const { t } = useTranslation()

    return (
        <>
            <Title>{t('Connect Your Lead Form')}</Title>
            <Paragraph>{t('Connect your Facebook account to receive candidates from lead ads.')}</Paragraph>
            <ConnectionCard className="mb-4" />
            <Paragraph>{t('You can also create the campaign now and connect Facebook later.')}</Paragraph>
        </>
    )
}

export function LinkFacebook() {
    const { t } = useTranslation()
    const { formProps } = useCreateCampaignStepsContext()
    const accountId = Form.useWatch('accountId', formProps.form)
    const formId = Form.useWatch('formId', formProps.form)
    const checkLeadSetup = useCheckFacebookLeadSetup()
    const [leadSetupResult, setLeadSetupResult] = useState<SetupCheckResult | null>(null)

    useEffect(() => {
        setLeadSetupResult(null)
    }, [accountId, formId])

    return (
        <>
            <Title>{t('Connect Your Lead Form')}</Title>
            <Paragraph>{t('Choose the Facebook page and lead form for this campaign.')}</Paragraph>

            <FormItem<StoreInput>
                name="accountId"
                label={t('Facebook page')}
            >
                <FacebookAccountSelect
                    onChange={() => {
                        formProps.form.setFieldValue('formId', undefined)
                    }}
                />
            </FormItem>

            {accountId && (
                <>
                    <FormItem<StoreInput>
                        name="formId"
                        label={t('Lead form')}
                    >
                        <LeadgenFormSelect accountId={accountId ?? undefined} />
                    </FormItem>
                    <Button
                        className="mb-4"
                        loading={checkLeadSetup.isPending}
                        onClick={() =>
                            checkLeadSetup
                                .mutateAsync({ accountId: accountId ?? undefined, formId: formId ?? undefined })
                                .then((checkResult) => setLeadSetupResult(checkResult))
                        }
                    >
                        {t('Validate lead setup')}
                    </Button>
                </>
            )}

            {leadSetupResult && (
                <SetupCheckResults
                    className="mb-4"
                    result={leadSetupResult}
                    successMessage={t('Lead setup is ready')}
                    warningMessage={t('Lead setup needs attention')}
                />
            )}
        </>
    )
}

function CreationBranding({ value, onChange }: { value?: Configuration; onChange?: (value: Configuration) => void }) {
    const { t } = useTranslation()
    const [open, setOpen] = useState(false)
    if (!value || !onChange) return null
    return (
        <Flex
            vertical
            gap="middle"
            className="mt-4"
        >
            <label>
                <Typography.Text>{t('Heading')}</Typography.Text>
                <Input
                    aria-label={t('Heading')}
                    value={value.heading}
                    maxLength={180}
                    onChange={(e) => onChange({ ...value, heading: e.target.value })}
                />
            </label>
            <Button onClick={() => setOpen(true)}>{t('Customize public form')}</Button>
            <Typography.Text type="secondary">
                {t('Logo upload and publishing are available in Candidate sources after creation.')}
            </Typography.Text>
            <Drawer
                destroyOnHidden
                title={t('Customize public form')}
                open={open}
                onClose={() => setOpen(false)}
                width="min(1200px, 100vw)"
                extra={
                    <Button
                        type="primary"
                        onClick={() => setOpen(false)}
                    >
                        {t('Done')}
                    </Button>
                }
            >
                <Typography.Paragraph type="secondary">
                    {t('Your form is created as a draft. Review, publish, and share it after creating the campaign.')}
                </Typography.Paragraph>
                <BrandingEditor
                    value={value}
                    onChange={onChange}
                />
            </Drawer>
        </Flex>
    )
}
